import React, { useContext, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import LabelsTag from '@components/framework/tags/LabelsTag.tsx';
import {
  CompanyModuleEnum,
  EntityTypeEnum,
  GlobalTimeslots,
  LabelPreviewWithoutTypeOutDTO,
  LovCompanyLocationWithOpeningHours,
  LovDTO,
  OpeningEntityTypeEnum,
  PermissionTypesEnum,
  TransportationTypeEnum,
} from '@api/ringil-api.v2.tsx';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import SvgLkw from '@components/ringil3/icons/heading/Lkw.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { validation } from '@app/pages/dap/validation.ts';
import useForm from '@hooks/useForm/useForm.tsx';
import { isNullOrUndefined } from '@utils/utils.tsx';
import ShipmentNumberTag from '@app/pages/dap/tags/ShipmentNumberTag.tsx';
import CarrierNameTag from '@app/pages/dap/tags/CarrierNameTag.tsx';
import InputTransportationType from '@components/framework/input/InputTransportationType/InputTransportationType.tsx';
import Card from '@components/framework/card/Card.tsx';
import SvgMapPin from '@components/framework/icons/MapPin.tsx';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import SvgCalendar from '@app/pages/dap/icons/Calendar.tsx';
import InputEntityOwner from '@components/obsolete/Form/InputEntityOwner/InputEntityOwner.tsx';
import InputInheriterCompany from '@components/obsolete/Form/InputEntityOwner/InputInheriterCompany.tsx';
import InputCompanyLocation from '@components/obsolete/Form/InputEntityOwner/InputCompanyLocation.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import ManageTimeslot from '@components/obsolete/TimeslotWithDefaults/ManageTimeslot.tsx';
import OrdersComponent from '@components/framework/orders/OrdersComponent.tsx';
import { Order } from '@components/framework/orders/common.tsx';
import AttachmentsSection from '@app/pages/attachment/AttachmentsSection.tsx';
import { UploadedAttachment } from '@components/ringil3/Features/Attachments/UploadAttachmentDialog.tsx';
import SvgInternalInfoHeadling from '@components/framework/icons/InternalInfoHeadling.tsx';
import ResponsiblePersonTag from '@components/framework/tags/ResponsiblePersonTag.tsx';
import InternalInfoNotifyTag from '@components/framework/tags/InternalInfoNotifyTag.tsx';
import InternalInfoNotifyByRoleTag from '@components/framework/tags/InternalInfoNotifyByRoleTag.tsx';
import { RoleEnum } from '@type/enums.ts';
import InternalInfoGoodsInvoiceTag, { GoodsInvoiceDTO } from '@components/framework/tags/InternalInfoGoodsInvoiceTag.tsx';
import InternalNoteTag from '@components/framework/tags/InternalNoteTag.tsx';
import RingilButton from '@components/framework/buttons/RingilButton.tsx';
import { findValidationMessage } from '@utils/validation.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import { dayjsFromLocalDateString, formatSimpleDateTimeFromDayJs } from '@utils/dateWithTz';
import { dapOrderConfig } from '@app/pages/dap/config/orders.ts';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import InternalInfoFoldersTag from '@components/framework/tags/InternalInfoFoldersTag.tsx';
import { buildDapSupplierNotificationRequest } from '@app/pages/dap/createSupplierNotificationDapMapping.ts';

interface ComponentProps {
  initialData: SupplierNotificationDapForm;
}

export interface SupplierNotificationDapForm {
  companyId: string;
  supplierId: string;
  carrierName: string;
  attachments: UploadedAttachment[];
  notifyData?: {
    responsiblePerson?: LovDTO;
    userToNotify?: LovDTO[];
    notifyUsersDependsOnRole: boolean;
  };
  transportationType: TransportationTypeEnum;
  shipmentNumber: string;
  orders: Order[];
  supplierLocationId: string;
  customerLocationId: string;
  internalNote?: string;
  loadingGlobalTimeslots: GlobalTimeslots;
  unloadingGlobalTimeslots: GlobalTimeslots;
  customerLocation?: LovCompanyLocationWithOpeningHours;
  supplierLocation?: LovCompanyLocationWithOpeningHours;
  goodsInvoice?: GoodsInvoiceDTO;
  labels: LabelPreviewWithoutTypeOutDTO[];
  folders?: LovDTO[];
}

const CreateSupplierNotificationDap: React.FC<ComponentProps> = ({ initialData }) => {
  const { createDAPSupplierNotification } = useContext(ApiContext);
  const [workedApiRequests, setWorkedApiRequests] = useState(0);
  const { tr } = useTranslationLgs();
  const nav = useNavigation();
  const { loggedUser, hasPermission } = useContext(AuthenticatedUserContext);

  const form = useForm<SupplierNotificationDapForm>(validation(tr, dapOrderConfig), initialData, d => save(d), false, false);
  const isOwner = loggedUser?.companyId === form.values?.companyId;

  const save = (data: SupplierNotificationDapForm) => {
    /**
     *  companyId: string;
     *     supplierId: string;
     *     attachments: AttachmentIdWithTypeV2InDTO[];
     *     responsiblePersonId?: string;
     *     userIdsToNotify?: string[];
     *     notifyUsersDependsOnRole: boolean;
     *     transportationType: TransportationTypeEnum;
     *     carrierName: string;
     *     shipmentNumber: string;
     *     orders: GlobalOrdersRequest[];
     *     supplierLocationId: string;
     *     customerLocationId: string;
     *     internalInvoiceNumber?: string;
     *     invoiceDueDate?: string;
     *     deliveryConditions?: string;
     *     internalNote?: string;
     *     note?: string;
     *     loadingGlobalTimeslots: GlobalTimeslotsDTO;
     *     unloadingGlobalTimeslots: GlobalTimeslotsDTO;
     *     labelIds: string[];
     */
    createDAPSupplierNotification(
      buildDapSupplierNotificationRequest(data, form.values),
      () => {
        nav.navigate(nav.urlFunctions.createSupplierNotificationList());
      },
    );
  };

  const disabled = !hasPermission([PermissionTypesEnum.PERMISSION_SN_DXX_CREATE]);

  if (isNullOrUndefined(form?.values) || isNullOrUndefined(loggedUser)) return <></>;

  return (
    <>
      <div className={'flex flex-col gap-4'}>
        <BreadCrumbComponent
          items={[
            { label: tr(`CreateSupplierNotificationDap.supplierNotificationList`, `Avizace`) },
            { label: tr(`CreateSupplierNotificationDap.createSupplierNotificationDap`, `Vytvořit DAP avizaci`) },
          ]}
        />
        <Heading
          title={tr(`CreateSupplierNotificationDap.heading`, `Nová avizace`)}
          headingVariant={'h1'}
          labels={
            <>
              {isOwner && (
                <LabelsTag
                  objectId={null}
                  objectType={EntityTypeEnum.SUPPLIER_NOTIFICATION}
                  resolveLabelsForType={EntityTypeEnum.SUPPLIER_NOTIFICATION}
                  onChange={labels => {
                    form.setFieldValue(form.names.labels, labels);
                  }}
                  value={form.values.labels}
                  disabled={disabled}
                />
              )}
            </>
          }
          buttons={[]}
        />

        <COHeadline variant={'h2'} title={tr('CreateSupplierNotificationDap.informaceKPreprave', 'Informace k přepravě')} icon={<SvgLkw />} />
        <InputTransportationType
          onChange={v => {
            form.setFieldValue(form.names.transportationType, v);
          }}
          value={form.values.transportationType}
          disabled={disabled}
        />

        <div className={'flex flex-row gap-2'}>
          <ShipmentNumberTag
            value={form.values.shipmentNumber}
            onChange={v => form.setFieldValue(form.names.shipmentNumber, v)}
            errorMessage={findValidationMessage(form.validationErrors, 'shipmentNumber', tr)}
            readonly={disabled}
          />

          <CarrierNameTag
            value={form.values.carrierName}
            onChange={v => form.setFieldValue(form.names.carrierName, v)}
            errorMessage={findValidationMessage(form.validationErrors, 'carrierName', tr)}
            readonly={disabled}
          />
        </div>
        <SectionPanel icon={<SvgCalendar />} heading={tr('CreateSupplierNotificationDap.oAvizaci', 'O avizaci')}>
          <div className={'grid grid-cols-2 gap-4'}>
            <div className={'flex flex-col gap-2'}>
              <Card>
                <div className={'flex flex-col gap-4'}>
                  <COHeadline title={tr('CreateSupplierNotificationDap.adresaNakladky', 'Adresa nakládky')} variant={'h4'} icon={<SvgMapPin />} />
                  <Field
                    label={tr('CreateSupplierNotificationDap.odesilatel', 'Odesílatel')}
                    errorMessage={findValidationMessage(form.validationErrors, 'supplierId', tr)}
                  >
                    <InputInheriterCompany
                      companyId={form.values.companyId}
                      inheriterId={form.values.supplierId}
                      onChange={v => form.setFieldValue('supplierId', v)}
                      module={CompanyModuleEnum.SUPPLIER_NOTIFICATION}
                      readonly={disabled}
                    />
                  </Field>
                  <Field
                    label={tr('CreateSupplierNotificationDap.nazevLokace', 'Název lokace')}
                    errorMessage={findValidationMessage(form.validationErrors, 'supplierLocationId', tr)}
                  >
                    <InputCompanyLocation
                      moduleName={CompanyModuleEnum.SUPPLIER_NOTIFICATION}
                      companyId={form.values.supplierId}
                      companyLocationId={form.values.supplierLocationId}
                      onChange={v => {
                        form.setFieldValue(form.names.supplierLocationId, v?.id);
                        form.setFieldValue(form.names.supplierLocation, v);
                      }}
                      style={{ width: '100%' }}
                      disabled={disabled}
                      filterPartnerLocation={true}
                    />
                  </Field>
                  <Field
                    label={tr('CreateSupplierNotificationDap.datumVyzvednuti', 'Datum vyzvednutí')}
                    errorMessage={findValidationMessage(form.validationErrors, 'loadingGlobalTimeslots', tr)}
                  >
                    <ManageTimeslot
                      externalLabel
                      entityType={OpeningEntityTypeEnum.LOCATION}
                      entityId={form.values.supplierLocationId}
                      onChange={v => {
                        form.setFieldValue(form.names.loadingGlobalTimeslots, { ...v, timezoneId: form.values.supplierLocation?.timezoneId });
                      }}
                      timeslot={form.values.loadingGlobalTimeslots}
                      validationErrors={form.validationErrors}
                      validationPrefix={'loadingGlobalTimeslots.'}
                    />
                  </Field>
                </div>
              </Card>
              <Card>
                <div className={'flex flex-col gap-4'}>
                  <COHeadline title={tr('CreateSupplierNotificationDap.adresaVykladky', 'Adresa vykládky')} variant={'h4'} icon={<SvgMapPin />} />
                  <Field label={tr('CreateSupplierNotificationDap.prijemce', 'Příjemce')} errorMessage={findValidationMessage(form.validationErrors, 'companyId', tr)}>
                    <InputEntityOwner
                      companyId={form.values.companyId}
                      onChange={c => form.setFieldValue('companyId', c)}
                      moduleType={CompanyModuleEnum.SUPPLIER_NOTIFICATION}
                      style={{ width: '100%' }}
                      onChangeCompanyCode={v => form.setFieldValue('companyCode', v)}
                      readonly={disabled}
                    />
                  </Field>
                  <Field
                    label={tr('CreateSupplierNotificationDap.nazevLokace', 'Název lokace')}
                    errorMessage={findValidationMessage(form.validationErrors, 'customerLocationId', tr)}
                  >
                    <InputCompanyLocation
                      moduleName={CompanyModuleEnum.SUPPLIER_NOTIFICATION}
                      companyId={form.values.companyId}
                      companyLocationId={form.values.customerLocationId}
                      onChange={v => {
                        form.setFieldValue(form.names.customerLocationId, v?.id);
                        form.setFieldValue(form.names.customerLocation, v);
                      }}
                      style={{ width: '100%' }}
                      disabled={disabled}
                      filterPartnerLocation={true}
                    />
                  </Field>
                  <Field label={tr('CreateSupplierNotificationDap.datumDodani', 'Datum dodání')}>
                    <ManageTimeslot
                      externalLabel
                      entityType={OpeningEntityTypeEnum.LOCATION}
                      entityId={form.values.customerLocationId}
                      onChange={v => {
                        form.setFieldValue(form.names.unloadingGlobalTimeslots, { ...v, timezoneId: form.values.customerLocation?.timezoneId });
                      }}
                      timeslot={form.values.unloadingGlobalTimeslots}
                      validationErrors={form.validationErrors}
                      validationPrefix={'unloadingGlobalTimeslots.'}
                    />
                  </Field>
                </div>
              </Card>
            </div>
            <Card>
              <COHeadline title={tr('CreateSupplierNotificationDap.objednavka', 'Objednávka')} variant={'h4'} />
              <OrdersComponent
                orders={form.values.orders}
                companyId={form.values.companyId}
                onChange={v =>
                  form.useProduce(d => {
                    d.orders = v;
                  })
                }
                config={dapOrderConfig}
                validationErrors={form.validationErrors}
              />
            </Card>
          </div>
        </SectionPanel>
        <AttachmentsSection
          attachments={form.values.attachments}
          onChange={v => form.setFieldValue(form.names.attachments, v)}
          disabled={disabled}
          loading={workedApiRequests > 0}
        />

        {isOwner && (
          <>
            <COHeadline variant={'h2'} title={tr('CreateSupplierNotificationDap.interniInfo', 'Interní info')} icon={<SvgInternalInfoHeadling />} />
            <div className={'flex flex-row gap-4'}>
              <ResponsiblePersonTag
                person={form.values.notifyData?.responsiblePerson}
                onChange={v => {
                  form.setFieldValue(form.names.notifyData + '.responsiblePerson', v);
                }}
              />

              <InternalInfoNotifyTag
                persons={form.values.notifyData?.userToNotify}
                onChange={v => {
                  form.setFieldValue(form.names.notifyData + '.userToNotify', v);
                }}
              />

              <InternalInfoNotifyByRoleTag
                notifyRoleName={RoleEnum.ROLE_SN_NOTIFY}
                isActive={form.values.notifyData?.notifyUsersDependsOnRole}
                onChange={v => {
                  form.setFieldValue(form.names.notifyData + '.notifyUsersDependsOnRole', v);
                }}
              />
            </div>
            <div className={'flex flex-row gap-4'}>
              <InternalInfoGoodsInvoiceTag
                value={form.values.goodsInvoice}
                onChange={v => form.setFieldValue(form.names.goodsInvoice, v)}
                validationErrors={form.validationErrors}
                validationPrefix={'goodsInvoice'}
              />

              <InternalInfoFoldersTag
                folders={form.values.folders ?? []}
                onChange={v => {
                  form.useProduce(d => {
                    d.folders = v;
                  });
                }}
                entityType={EntityTypeEnum.SUPPLIER_NOTIFICATION}
              />

              <InternalNoteTag value={form.values.internalNote} onChange={v => form.setFieldValue(form.names.internalNote, v)} readonly={false} required={false} />
            </div>
          </>
        )}

        <div className={'flex flex-row gap-8 items-center'}>
          <RingilButton label={tr('CreateSupplierNotificationDap.vytvorit', 'Vytvořit')} mode={'filled-big'} onClick={() => form.validateAndSend()} disabled={disabled}/>
        </div>
      </div>
    </>
  );
};

export default CreateSupplierNotificationDap;
